import {
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence } from "motion/react";

import { MotionBox, motionDurations, popoverVariants } from "@hightouch/htui";

import { isPopupLayerOpen } from "src/components/layout/popup-layers";
import {
  portalsWithOpenOverlay,
  topOpenOverlay,
} from "src/components/layout/settings-overlays";
import { ViewInsetProvider } from "src/components/layout/view-height";
import { lockDocumentScroll } from "src/utils/document-scroll-lock";

// The element React renders the app into, which holds the page under settings.
const APP_ROOT_ID = "react";

const TABBABLE =
  'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

// The panel's gap from each edge of the viewport, which shows the scrim around it.
const PANEL_INSET = 6;
const PANEL_INSET_LENGTH = `var(--chakra-space-${PANEL_INSET})`;

// The panel enters as htui's Dialog does.
const panelMotion = { ...popoverVariants, animate: "enter", exit: "exit" };

const backdropMotion = {
  initial: { opacity: 0 },
  // Enters and exits over the panel's durations, so the pair moves together.
  animate: { opacity: 1, transition: { duration: motionDurations.slow } },
  exit: { opacity: 0, transition: { duration: motionDurations.fast } },
};

type SettingsViewProps = {
  /** Whether settings show. Closing plays the exit before `onCloseComplete`. */
  isOpen: boolean;
  /** False when a page load or history move restored settings, which shouldn't play the entrance. */
  animateOnMount: boolean;
  /** Called once settings have animated closed. */
  onCloseComplete: () => void;
  /** Called once settings have animated open. */
  onOpenComplete?: () => void;
  /** Keys that reach the view, including those pressed on the view itself, before Escape stops here. */
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  /** Called when a click lands on the scrim around settings, which closes them as Escape does. */
  onScrimClick: () => void;
  /** The dialog's accessible name: the open sidebar's title. */
  label: string;
  /** Takes focus as settings open, as a modal's `initialFocusRef` does, unless a drawer or dialog opened over them. Defaults to their first control. */
  initialFocusRef?: RefObject<HTMLElement>;
  children: ReactNode;
};

/**
 * Settings over the page, in a panel inset from the viewport on a scrim, as htui's Dialog sits. It is on the `overlay`
 * layer, below drawers (`modal`) and dialogs, menus, and popovers (`popover`), and it opens no modal of its own, so
 * anything a settings page opens portals to the body as it would on a page and lands on top without raising its own layer.
 *
 * The page underneath stays mounted but is made inert, as a native modal dialog makes the rest of the document, rather
 * than trapping focus: a trap would fight the ones in the dialogs and drawers settings pages open. The document's
 * scroll is locked so a wheel that settings don't use can't move the page behind them, which keeps it where it was.
 * Drawers and dialogs the page had open portal outside it, so they hide with it until settings close.
 */
export const SettingsView = ({
  isOpen,
  animateOnMount,
  onCloseComplete,
  onOpenComplete,
  onKeyDown,
  onScrimClick,
  label,
  initialFocusRef,
  children,
}: SettingsViewProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  // Whether the press now on the scrim began there with no drawer, dialog, menu or popover over settings to dismiss first.
  const scrimPressRef = useRef(false);
  // Read while rendering, before anything a settings page opens on mount has portaled in beside them.
  const [pageOverlays] = useState(portalsWithOpenOverlay);

  // While settings show, the page under them takes no input and its document doesn't scroll. Focus moves in, as a
  // modal's would, and back to the opener when settings close, including when they unmount without an exit.
  useEffect(() => {
    if (!isOpen) return;
    // Read before the page goes inert, which blurs whatever on it had focus.
    const opener = document.activeElement;
    const appRoot = document.getElementById(APP_ROOT_ID);
    const wasInert = appRoot?.inert ?? false;
    const releaseScroll = lockDocumentScroll();
    if (appRoot) appRoot.inert = true;
    const hiddenOverlays = pageOverlays.map((portal) => ({
      portal,
      wasHidden: portal.hidden,
      wasInert: portal.inert,
    }));
    for (const { portal } of hiddenOverlays) {
      portal.hidden = true;
      portal.inert = true;
    }
    // A hidden drawer's scroll lock cancels wheels outside it, so settings' own stop at the body while nothing covers them.
    const keepScrollInSettings = (event: Event) => {
      const dialog = dialogRef.current;
      if (
        dialog &&
        event.target instanceof Node &&
        dialog.contains(event.target) &&
        !topOpenOverlay()
      ) {
        event.stopPropagation();
      }
    };
    if (hiddenOverlays.length > 0) {
      document.body.addEventListener("wheel", keepScrollInSettings, {
        passive: true,
      });
      document.body.addEventListener("touchmove", keepScrollInSettings, {
        passive: true,
      });
    }
    const frame = requestAnimationFrame(() => {
      const dialog = dialogRef.current;
      const focused = document.activeElement;
      // Whatever took focus as settings opened keeps it, such as a field or a wizard drawn over them.
      if (
        !dialog ||
        (focused && focused !== document.body && focused !== opener)
      ) {
        return;
      }
      const overlay = topOpenOverlay();
      const initial = overlay ? null : initialFocusRef?.current;
      const top = overlay ?? dialog;
      (initial ?? top.querySelector<HTMLElement>(TABBABLE) ?? top).focus();
    });
    return () => {
      cancelAnimationFrame(frame);
      document.body.removeEventListener("wheel", keepScrollInSettings);
      document.body.removeEventListener("touchmove", keepScrollInSettings);
      releaseScroll();
      if (appRoot) appRoot.inert = wasInert;
      for (const { portal, wasHidden, wasInert } of hiddenOverlays) {
        portal.hidden = wasHidden;
        portal.inert = wasInert;
      }
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, [isOpen, pageOverlays, initialFocusRef]);

  // Escape that settings didn't handle stops here, so listeners on the page underneath never see it.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (event.key === "Escape") event.stopPropagation();
  };

  // Read on pointerdown: a menu or popover over settings closes on the press, before its click reaches the scrim.
  const handleScrimPointerDown = () => {
    scrimPressRef.current = !topOpenOverlay() && !isPopupLayerOpen();
  };
  const handleScrimClick = () => {
    const pressedOnScrim = scrimPressRef.current;
    scrimPressRef.current = false;
    if (pressedOnScrim) onScrimClick();
  };

  return createPortal(
    <AnimatePresence initial={animateOnMount} onExitComplete={onCloseComplete}>
      {isOpen && (
        <MotionBox
          key="backdrop"
          position="fixed"
          inset={0}
          zIndex="overlay"
          bg="blackAlpha.40"
          backdropFilter="blur(4px)"
          onPointerDown={handleScrimPointerDown}
          onClick={handleScrimClick}
          {...backdropMotion}
        />
      )}
      {isOpen && (
        <MotionBox
          key="settings"
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={label}
          position="fixed"
          inset={PANEL_INSET}
          zIndex="overlay"
          display="flex"
          flexDirection="column"
          bg="white"
          borderRadius="xl"
          boxShadow="lg"
          overflow="hidden"
          overscrollBehavior="contain"
          // Focusable for the open handoff when a page has no control to take focus.
          tabIndex={-1}
          outline="none"
          // Outward, onto the scrim: the page inside covers the whole panel, which would hide an inset ring.
          _focusVisible={{ boxShadow: "outline" }}
          onKeyDown={handleKeyDown}
          onAnimationComplete={(definition) => {
            if (definition === "enter") onOpenComplete?.();
          }}
          {...panelMotion}
        >
          <ViewInsetProvider value={PANEL_INSET_LENGTH}>
            {children}
          </ViewInsetProvider>
        </MotionBox>
      )}
    </AnimatePresence>,
    document.body,
  );
};
